import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, message, Typography, Space, Input, Select, Modal, Tag } from 'antd';
import { getIndicadoresProyectos, asignarIndicadoresProyecto, getIndicadoresAsignadosProyecto } from '../../../services/indicadoresPO/indicadoresService';
import './Asignaciones.css';
import { useSelector } from 'react-redux';
import { selectAppPermissions } from '../../../store/auth/selectors';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function AsignarIndicadores({ oidProyecto, onClose }) {
    const [indicadores, setIndicadores] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAsignar, setLoadingAsignar] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState(undefined);
    const [isModalVisible, setIsModalVisible] = useState(false);


    //Control de Permisos
    const appName = 'asignarIndicadoresPO';
    const permissions = useSelector((state) => selectAppPermissions(state, appName));
    const permissionInsert = permissions.priv_insert === 'Y';

    // 1️⃣ Cargar indicadores
    const fetchIndicadores = async () => {
        setLoading(true);
        try {
            const { status, data } = await getIndicadoresProyectos();

            if (status === 200) {
                const { status: statusAsignados, data: asignados } = await getIndicadoresAsignadosProyecto(oidProyecto);

                if (statusAsignados === 200) {
                    const disponibles = data.filter(ind => !asignados.includes(ind.oid));
                    setIndicadores(disponibles);
                    message.success('Indicadores cargados correctamente.');
                } else {
                    setIndicadores(data);
                    message.warning('No se pudieron verificar los indicadores asignados.');
                }
            } else {
                message.warning('No se pudieron cargar los indicadores.');
            }
        } catch (error) {
            console.error('Error al cargar indicadores:', error);
            message.error('Error al cargar indicadores.');
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        fetchIndicadores();
    }, [oidProyecto]);

    // 2️⃣ Filtros y búsqueda
    const tiposUnicos = useMemo(() => {
        const tipos = indicadores.map(item => item.tipo);
        return [...new Set(tipos)];
    }, [indicadores]);

    const indicadoresFiltrados = useMemo(() => {
        return indicadores.filter(item => {
            const matchTipo = tipoFiltro ? item.tipo === tipoFiltro : true;
            const matchSearch = item.indicador.toLowerCase().includes(searchText.toLowerCase());
            return matchTipo && matchSearch;
        });
    }, [indicadores, tipoFiltro, searchText]);

    const limpiarFiltros = () => {
        setSearchText('');
        setTipoFiltro(undefined);
    };

    // 3️⃣ Selección
    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
    };

    // 4️⃣ Confirmar asignación con verificación
    const handleAsignar = () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Debes seleccionar al menos un indicador.');
            return;
        }

        // Mostrar el modal de confirmación
        setIsModalVisible(true);
    };

    // 5️⃣ Columnas
    const columns = [
        {
            title: 'ID',
            dataIndex: 'oid',
            key: 'oid',
            width: 20,
            align: 'center',
        },
        {
            title: 'Nombre del Indicador',
            dataIndex: 'indicador',
            key: 'indicador',
            width: 200,
            align: 'left',
        },
        {
            title: 'Unidad de Medida',
            dataIndex: 'unidad_medida',
            key: 'unidad_medida',
            width: 100,
            align: 'center',
        },
        {
            title: 'Tipo de Indicador',
            dataIndex: 'tipo',
            key: 'tipo',
            width: 150,
            align: 'center',
        },
    ];

    // Deriva la lista de seleccionados
    const indicadoresSeleccionados = indicadores.filter(ind =>
        selectedRowKeys.includes(ind.oid)
    );




    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Title level={4}>Selecciona los indicadores que trabajará este proyecto</Title>

            <Space style={{ marginBottom: 8, flexWrap: 'wrap' }}>
                <Search
                    className="search-indicadores"
                    placeholder="Buscar por nombre del indicador"
                    allowClear
                    onChange={e => setSearchText(e.target.value)}
                    value={searchText}
                    style={{ width: 250 }}
                />

                <Select
                    placeholder="Filtrar por tipo"
                    allowClear
                    value={tipoFiltro}
                    onChange={setTipoFiltro}
                    style={{ width: 200 }}
                >
                    {tiposUnicos.map(tipo => (
                        <Option key={tipo} value={tipo}>{tipo}</Option>
                    ))}
                </Select>

                <Button onClick={limpiarFiltros}>
                    Limpiar filtros
                </Button>

                {permissionInsert && (
                    <Button
                        type="primary"
                        onClick={handleAsignar}
                        loading={loadingAsignar}
                        disabled={selectedRowKeys.length === 0}
                    >
                        Asignar Indicadores
                    </Button>
                )}
            </Space>

            <Table
                rowKey="oid"
                dataSource={indicadoresFiltrados}
                columns={columns}
                loading={loading}
                rowSelection={rowSelection}
                pagination={true}
                scroll={{ y: 400 }}
                size="middle"
            />
            <Modal
                title="¿Confirmas asignar los siguientes indicadores?"
                open={isModalVisible}
                onOk={async () => {
                    setLoadingAsignar(true);
                    try {
                        const payload = {
                            oidProyecto,
                            indicadores: selectedRowKeys,
                        };
                        await asignarIndicadoresProyecto(payload);
                        message.success('Indicadores asignados correctamente.');
                        fetchIndicadores();
                        setSelectedRowKeys([]);
                        setIsModalVisible(false);
                    } catch (error) {
                        console.error('Error al asignar indicadores:', error);
                        message.error('Error al asignar indicadores.');
                    } finally {
                        setLoadingAsignar(false);
                    }
                }}
                onCancel={() => setIsModalVisible(false)}
                okText="Asignar"
                cancelText="Cancelar"
                width="80%"
                maxWidth="80%"
                // 💡 Muestra el spinner en el OK mientras loadingAsignar = true
                confirmLoading={loadingAsignar}
                // 💡 Deshabilita el botón de Asignar si no tienes permiso
                okButtonProps={{ disabled: !permissionInsert }}
            >
                <div
                    style={{
                        maxHeight: '500px',
                        overflowY: 'auto',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        paddingTop: '8px',
                        wordBreak: 'break-word',
                    }}
                >
                    {indicadoresSeleccionados.map(ind => (
                        <Tag key={ind.oid} color="blue">
                            {ind.indicador}
                        </Tag>
                    ))}
                </div>
            </Modal>
        </Space>
    );
}

